import { useEffect, useRef, useState, useCallback } from "react";
import { Camera, X, Check, RotateCcw, Loader2, Crop, Undo2 } from "lucide-react";
import PerspT from "perspective-transform";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { api } from "../../lib/api";

/**
 * CameraCaptureDialog — live camera preview to capture a photo of an invoice.
 *
 * Props:
 * - open: boolean
 * - onOpenChange: (open: boolean) => void
 * - onCapture: (file: File) => void  // called when user confirms a shot
 */
export function CameraCaptureDialog({ open, onOpenChange, onCapture }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [snapshot, setSnapshot] = useState(null); // dataURL of RAW capture
  const [cropped, setCropped] = useState(null);   // dataURL of auto-cropped version
  const [detecting, setDetecting] = useState(false);
  const [useRaw, setUseRaw] = useState(false);    // user preference: use cropped or raw
  const [facing, setFacing] = useState("environment"); // or "user"

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startStream = useCallback(async () => {
    setStarting(true);
    setError(null);
    setSnapshot(null);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("Tu navegador no soporta acceso a la cámara. Usa el botón de subir archivo.");
      }
      const constraints = {
        video: {
          facingMode: { ideal: facing },
          width: { ideal: 1920 },
          height: { ideal: 1440 },
        },
        audio: false,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
    } catch (e) {
      const msg = e?.message || "";
      let friendly = "No se pudo acceder a la cámara. Usa el botón de subir archivo.";
      if (/not allowed|permission/i.test(msg)) friendly = "Permiso de cámara denegado. Concede el permiso y vuelve a intentarlo, o sube el archivo.";
      else if (/not found|no such/i.test(msg)) friendly = "No se ha detectado ninguna cámara en este dispositivo. Usa el botón de subir archivo.";
      else if (/in use|busy/i.test(msg)) friendly = "La cámara está siendo usada por otra aplicación. Ciérrala e intenta de nuevo.";
      setError(friendly);
    } finally {
      setStarting(false);
    }
  }, [facing]);

  useEffect(() => {
    if (open) {
      startStream();
    } else {
      stopStream();
      setSnapshot(null);
      setCropped(null);
      setDetecting(false);
      setUseRaw(false);
      setError(null);
    }
    return () => stopStream();
  }, [open, startStream, stopStream]);

  // Given a raw dataURL + corners [[x,y],[x,y],[x,y],[x,y]] normalized 0-1
  // produces a dewarped A4-like crop (max 1800px long side) as dataURL.
  const applyPerspectiveCrop = (rawDataUrl, corners) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      try {
        const W = img.naturalWidth, H = img.naturalHeight;
        const pts = corners.map(([x, y]) => [x * W, y * H]);
        // Compute output size from corner distances (avg of two sides)
        const dist = (a, b) => Math.hypot(a[0] - b[0], a[1] - b[1]);
        const topW = dist(pts[0], pts[1]);
        const botW = dist(pts[3], pts[2]);
        const leftH = dist(pts[0], pts[3]);
        const rightH = dist(pts[1], pts[2]);
        let outW = Math.round((topW + botW) / 2);
        let outH = Math.round((leftH + rightH) / 2);
        const maxSide = 1800;
        if (Math.max(outW, outH) > maxSide) {
          const scale = maxSide / Math.max(outW, outH);
          outW = Math.round(outW * scale);
          outH = Math.round(outH * scale);
        }
        const srcFlat = [pts[0][0], pts[0][1], pts[1][0], pts[1][1], pts[2][0], pts[2][1], pts[3][0], pts[3][1]];
        const dstFlat = [0, 0, outW, 0, outW, outH, 0, outH];
        // inverse transform: output pixel -> input pixel (sample source)
        const inv = PerspT(dstFlat, srcFlat);

        // Render via offscreen canvas pixel loop with bilinear sampling
        const srcCanvas = document.createElement("canvas");
        srcCanvas.width = W; srcCanvas.height = H;
        srcCanvas.getContext("2d").drawImage(img, 0, 0);
        const srcData = srcCanvas.getContext("2d").getImageData(0, 0, W, H);
        const dstCanvas = document.createElement("canvas");
        dstCanvas.width = outW; dstCanvas.height = outH;
        const dstCtx = dstCanvas.getContext("2d");
        const dst = dstCtx.createImageData(outW, outH);
        for (let y = 0; y < outH; y++) {
          for (let x = 0; x < outW; x++) {
            const [sx, sy] = inv.transform(x, y);
            if (sx < 0 || sy < 0 || sx >= W - 1 || sy >= H - 1) continue;
            const x0 = Math.floor(sx), y0 = Math.floor(sy);
            const dx = sx - x0, dy = sy - y0;
            const di = (y * outW + x) * 4;
            for (let c = 0; c < 4; c++) {
              const a = srcData.data[(y0 * W + x0) * 4 + c];
              const b = srcData.data[(y0 * W + x0 + 1) * 4 + c];
              const cc = srcData.data[((y0 + 1) * W + x0) * 4 + c];
              const d = srcData.data[((y0 + 1) * W + x0 + 1) * 4 + c];
              dst.data[di + c] = ((1 - dx) * (1 - dy) * a +
                                  dx * (1 - dy) * b +
                                  (1 - dx) * dy * cc +
                                  dx * dy * d) | 0;
            }
          }
        }
        dstCtx.putImageData(dst, 0, 0);
        resolve(dstCanvas.toDataURL("image/jpeg", 0.85));
      } catch (e) {
        reject(e);
      }
    };
    img.onerror = reject;
    img.src = rawDataUrl;
  });

  const handleShot = async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    const w = video.videoWidth || 1920;
    const h = video.videoHeight || 1440;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, w, h);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
    setSnapshot(dataUrl);
    stopStream();

    // Auto-crop with AI
    setDetecting(true);
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "capture.jpg", { type: "image/jpeg" });
      const res = await api.aiDetectDocumentCorners(file);
      if (res.found && res.corners && res.confidence >= 0.4) {
        const cropUrl = await applyPerspectiveCrop(dataUrl, res.corners);
        setCropped(cropUrl);
      }
    } catch (e) {
      // Silent — user can still use the raw capture
      console.warn("Auto-crop failed:", e);
    } finally {
      setDetecting(false);
    }
  };

  const handleRetake = () => {
    setSnapshot(null);
    setCropped(null);
    setUseRaw(false);
    startStream();
  };

  const handleConfirm = async () => {
    const dataUrl = (cropped && !useRaw) ? cropped : snapshot;
    if (!dataUrl) return;
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], `factura_${Date.now()}.jpg`, { type: "image/jpeg" });
      onCapture?.(file);
      onOpenChange?.(false);
    } catch {
      toast.error("No se pudo preparar la foto");
    }
  };

  const handleFlip = () => {
    stopStream();
    setFacing((f) => (f === "environment" ? "user" : "environment"));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="camera-capture-dialog">
        <DialogHeader className="p-5 pb-3">
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Camera className="w-5 h-5" /> Captura tu factura
          </DialogTitle>
        </DialogHeader>

        <div className="relative bg-black aspect-[4/3] flex items-center justify-center">
          {error ? (
            <div className="p-6 text-center text-white space-y-3">
              <p className="text-sm">{error}</p>
              <Button variant="outline" onClick={() => onOpenChange(false)} className="text-black" data-testid="camera-error-close">
                Cerrar
              </Button>
            </div>
          ) : snapshot ? (
            <div className="relative w-full h-full">
              <img
                src={(cropped && !useRaw) ? cropped : snapshot}
                alt="captura"
                className="w-full h-full object-contain"
                data-testid="camera-snapshot-preview"
              />
              {detecting && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm">
                  <div className="flex flex-col items-center gap-2 text-white">
                    <Loader2 className="w-7 h-7 animate-spin" />
                    <p className="text-sm font-medium">Detectando bordes de la factura…</p>
                  </div>
                </div>
              )}
              {cropped && !detecting && (
                <div
                  className="absolute top-3 left-3 bg-emerald-500/95 text-white text-[11px] font-semibold px-2.5 py-1 rounded-full flex items-center gap-1.5 shadow-lg"
                  data-testid="camera-crop-applied-badge"
                >
                  <Crop className="w-3 h-3" /> Recorte automático aplicado
                </div>
              )}
            </div>
          ) : (
            <>
              <video
                ref={videoRef}
                playsInline
                muted
                className="w-full h-full object-cover"
                data-testid="camera-video"
              />
              {starting && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                  <Loader2 className="w-8 h-8 text-white animate-spin" />
                </div>
              )}
              {/* Framing overlay */}
              <div className="absolute inset-6 border-2 border-white/50 rounded-lg pointer-events-none" />
              <p className="absolute bottom-3 left-0 right-0 text-center text-white/90 text-xs px-6 drop-shadow">
                Encuadra la factura dentro del recuadro, buena iluminación y sin reflejos.
              </p>
            </>
          )}
          <canvas ref={canvasRef} className="hidden" />
        </div>

        <div className="p-4 bg-background flex items-center justify-between gap-3">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="camera-cancel-btn">
            <X className="w-4 h-4 mr-1" /> Cancelar
          </Button>

          {snapshot ? (
            <div className="flex flex-wrap gap-2 items-center">
              {cropped && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setUseRaw((v) => !v)}
                  disabled={detecting}
                  className="text-xs gap-1.5 h-8"
                  data-testid="camera-toggle-original-btn"
                >
                  <Undo2 className="w-3.5 h-3.5" />
                  {useRaw ? "Ver recorte" : "Usar foto original"}
                </Button>
              )}
              <Button variant="outline" onClick={handleRetake} disabled={detecting} data-testid="camera-retake-btn">
                <RotateCcw className="w-4 h-4 mr-1" /> Repetir
              </Button>
              <Button onClick={handleConfirm} disabled={detecting} data-testid="camera-confirm-btn">
                <Check className="w-4 h-4 mr-1" /> {detecting ? "Procesando…" : "Usar esta foto"}
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={handleFlip}
                disabled={starting || error}
                title="Cambiar cámara"
                data-testid="camera-flip-btn"
              >
                <RotateCcw className="w-4 h-4" />
              </Button>
              <Button
                size="lg"
                onClick={handleShot}
                disabled={starting || !!error}
                className="gap-2"
                data-testid="camera-shoot-btn"
              >
                <Camera className="w-5 h-5" />
                Capturar
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
